// src/features/marketplace/pages/MyListings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../../firebase';
import { collection, query, where, orderBy, onSnapshot, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Car, Eye, EyeOff, Edit2, Trash2, MessageCircle } from 'lucide-react';
import { formatPrice } from '../../../utils/helpers';

export const MyListings = ({ showToast }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }

    // ✅ FIX: Pokaż WSZYSTKIE statusy (pending, active, hidden)
    const q = query(
      collection(db, "marketplace_listings"),
      where("userId", "==", auth.currentUser.uid),
      orderBy("createdAt", "desc")
    );

    return onSnapshot(q, (snap) => {
      setListings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [navigate]);

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'hidden' : 'active';
      await updateDoc(doc(db, "marketplace_listings", id), { 
        status: newStatus 
      });
      showToast(newStatus === 'active' ? "Ogłoszenie aktywne" : "Ogłoszenie ukryte");
    } catch (error) {
      console.error("Błąd zmiany statusu:", error);
      showToast("Błąd zmiany statusu", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Czy na pewno usunąć ogłoszenie?")) return;
    
    try {
      await deleteDoc(doc(db, "marketplace_listings", id));
      showToast("Ogłoszenie usunięte");
    } catch (error) {
      console.error("Błąd usuwania:", error);
      showToast("Błąd usuwania", "error");
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-gray-500">Ładowanie...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <div className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-black text-gray-900 mb-2">Moje ogłoszenia</h1>
          <p className="text-sm text-gray-600">Zarządzaj swoimi ogłoszeniami</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        {listings.length === 0 ? (
          <div className="text-center py-20">
            <Car className="mx-auto mb-4 text-gray-300" size={64}/>
            <p className="text-gray-500 font-semibold mb-2">Brak ogłoszeń</p>
            <p className="text-sm text-gray-400 mb-6">Dodaj pierwsze ogłoszenie</p>
            <button
              onClick={() => navigate('/market/sell')}
              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-teal-700 transition-all"
            >
              Dodaj ogłoszenie
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {listings.map(listing => (
              <div
                key={listing.id}
                className={`bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-all ${
                  listing.status === 'hidden' ? 'opacity-60' : ''
                }`}
              >
                <div className="flex gap-4 p-4">
                  {/* Image - ✅ FIX: Używaj thumbs */}
                  <div 
                    className="w-32 h-32 rounded-xl overflow-hidden bg-gray-100 shrink-0 cursor-pointer"
                    onClick={() => navigate(`/market/car/${listing.id}`)}
                  >
                    <img
                      src={listing.thumbs?.[0] || listing.images?.[0] || '/placeholder-car.jpg'}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => navigate(`/market/car/${listing.id}`)}
                      >
                        <h3 className="font-bold text-lg text-gray-900 mb-1 hover:text-emerald-600 transition-colors">
                          {listing.title}
                        </h3>
                        <p className="text-2xl font-black text-emerald-600">
                          {formatPrice(listing.price)}
                        </p>
                      </div>

                      {/* ✅ FIX: Pokaż wszystkie statusy */}
                      <div className={`px-3 py-1 rounded-lg text-xs font-bold ${
                        listing.status === 'active' 
                          ? 'bg-green-100 text-green-700'
                          : listing.status === 'pending_verification'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {listing.status === 'active' && 'Aktywne'}
                        {listing.status === 'pending_verification' && 'Oczekuje weryfikacji'}
                        {listing.status === 'hidden' && 'Ukryte'}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      <span>{listing.year}</span>
                      <span>•</span>
                      <span>{new Intl.NumberFormat('pl-PL').format(listing.mileage)} km</span>
                      <span>•</span>
                      <span>{listing.fuel}</span>
                    </div>

                    <p className="text-xs text-gray-500 mb-4">
                      Dodano: {listing.createdAt && new Date(listing.createdAt.seconds * 1000).toLocaleDateString('pl-PL')}
                    </p>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleToggleStatus(listing.id, listing.status)}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-semibold text-gray-700 transition-colors flex items-center gap-2"
                      >
                        {listing.status === 'active' ? (
                          <>
                            <EyeOff size={14}/> Ukryj
                          </>
                        ) : (
                          <>
                            <Eye size={14}/> Pokaż
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => navigate(`/market/messages?listing=${listing.id}`)}
                        className="px-3 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg text-xs font-semibold text-blue-700 transition-colors flex items-center gap-2"
                      >
                        <MessageCircle size={14}/> Wiadomości
                      </button>

                      <button
                        onClick={() => handleDelete(listing.id)}
                        className="px-3 py-2 bg-red-100 hover:bg-red-200 rounded-lg text-xs font-semibold text-red-700 transition-colors flex items-center gap-2"
                      >
                        <Trash2 size={14}/> Usuń
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
